import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Lock } from "lucide-react";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export const PaymentModal = ({ tier, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState("terms");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const effInr = couponInfo?.inr || tier.inr;
  const effUsd = couponInfo?.usd || tier.usd;

  const applyCoupon = async () => {
    setCouponError("");
    if (!coupon.trim()) return;
    if (!form.email.trim()) {
      setCouponError("Enter your email above first, then apply the code.");
      return;
    }
    setApplyingCoupon(true);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: coupon.trim(), tier: tier.key, email: form.email.trim(),
      });
      setCouponInfo(res.data);
    } catch (err) {
      setCouponInfo(null);
      setCouponError(err.response?.data?.detail || "This coupon code is not valid.");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    track("Checkout", "Initiate Checkout", { user_email: form.email });
    try {
      const res = await axios.post(`${API}/payments/create-order`, {
        tier: tier.key, ...form, coupon: couponInfo?.code || "",
      });
      setOrder(res.data);
      if (res.data.free) {
        onClose();
        navigate(`/intake/${res.data.intake_token}`);
      } else if (res.data.demo) {
        setStep("demo");
      } else {
        await openRazorpay(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create order. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const verifyAndUnlock = async (payload) => {
    const res = await axios.post(`${API}/payments/verify`, payload);
    track("Checkout", "Payment Success", { user_email: form.email });
    onClose();
    navigate(`/intake/${res.data.intake_token}`);
  };

  const openRazorpay = async (orderData) => {
    const ok = await loadRazorpayScript();
    if (!ok) {
      setError("Could not load Razorpay checkout. Check your connection.");
      return;
    }
    const rzp = new window.Razorpay({
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: "The Next Move by Rhellex",
      description: orderData.tier_name,
      order_id: orderData.order_id,
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#041C3E" },
      handler: async (response) => {
        try {
          await verifyAndUnlock({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
        } catch {
          setError("Payment verification failed. Contact support.");
        }
      },
      modal: {
        ondismiss: () => {
          setBusy(false);
          track("Checkout", "Payment Abandoned", { user_email: form.email });
          setError("Payment canceled. You can try again whenever you're ready.");
        },
      },
    });
    rzp.on("payment.failed", (resp) => {
      setBusy(false);
      track("Checkout", "Payment Failed", { user_email: form.email });
      setError(resp?.error?.description || "Payment failed. Please try another method or try again.");
    });
    rzp.open();
  };

  const demoPay = async () => {
    setBusy(true);
    try {
      await verifyAndUnlock({
        razorpay_order_id: order.order_id,
        razorpay_payment_id: `pay_demo_${Date.now()}`,
        razorpay_signature: "demo_signature",
      });
    } catch {
      setError("Demo payment failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="payment-modal"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-[#041C3E]/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-md bg-[#F9F6F1] border border-[#041C3E] p-8 font-montserrat text-[#162030]"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
        >
          <button
            data-testid="payment-modal-close"
            onClick={onClose}
            className="absolute top-4 right-4 text-[#041C3E] hover:text-[#D95A3D] transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>

          {step === "declined" ? (
            <div data-testid="payment-declined" className="text-center py-6">
              <h3 className="font-playfair text-2xl font-bold text-[#041C3E]">Thank you for checking us out.</h3>
              <p className="text-[#162030] mt-3 leading-relaxed">
                We will see you again whenever you are ready to make the next move.
              </p>
              <button
                data-testid="payment-declined-close"
                onClick={onClose}
                className="mt-6 bg-[#041C3E] text-white px-8 py-3 font-semibold tracking-wide hover:bg-[#D95A3D] transition-colors duration-300"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D95A3D]">
                {step === "terms" ? "Before you continue" : "Secure donation"}
              </p>
              <h3 className="font-playfair text-2xl font-bold text-[#041C3E] mt-2">{tier.name}</h3>
              <p className="text-[#162030] mt-1 text-sm">
                {couponInfo ? (
                  <>
                    <span className="line-through text-[#162030]/40">{tier.inr}</span>{" "}
                    <span className="font-bold text-[#041C3E]" data-testid="payment-discounted-price">{effInr}</span>
                    <span className="text-[#162030]/50"> / {effUsd}</span>
                  </>
                ) : (
                  <>{tier.inr} <span className="text-[#162030]/50">/ {tier.usd}</span></>
                )}
                {tier.recurring && <span className="text-[#162030]/50"> · per month</span>}
              </p>
              {tier.recurring && (
                <p data-testid="payment-recurring-note" className="mt-3 bg-white text-[#162030] text-xs px-4 py-3 leading-relaxed">
                  This is a monthly membership that renews automatically each month. You can cancel anytime.
                </p>
              )}
              <p data-testid="modal-no-refund" className="mt-3 border-l-4 border-[#D95A3D] bg-white px-4 py-3 text-xs font-semibold text-[#162030] leading-relaxed">
                Your full donation is required in advance to reserve your session. You may cancel for a full refund within 24 hours of your donation. Any cancellation after 24 hours from booking, or less than 36 hours before the session date, will receive only a partial refund.
              </p>

              {step === "terms" && (
                <div data-testid="payment-terms-step" className="mt-6 space-y-5">
                  <p className="text-sm text-[#162030] leading-relaxed">
                    Before you continue, please note this is coaching and mentoring guidance only, with no
                    guarantee of admission, job offers, placement, or intake. Your full donation is required in advance.
                    You may cancel for a full refund within 24 hours of your donation; after that a partial refund applies
                    per the policy. Please read the full{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-[#041C3E] underline underline-offset-2 hover:text-[#D95A3D]">
                      Terms and Conditions
                    </a>. Do you agree to proceed?
                  </p>
                  <div className="flex gap-3">
                    <button
                      data-testid="payment-terms-agree"
                      onClick={() => { setAgreed(true); setStep("details"); }}
                      className="flex-1 bg-[#D95A3D] text-white px-6 py-3 font-semibold tracking-wide hover:bg-[#C24A2F] transition-colors duration-300"
                    >
                      Agree
                    </button>
                    <button
                      data-testid="payment-terms-disagree"
                      onClick={() => setStep("declined")}
                      className="flex-1 border border-[#041C3E] text-[#041C3E] px-6 py-3 font-semibold hover:bg-[#041C3E] hover:text-white transition-colors duration-300"
                    >
                      Disagree
                    </button>
                  </div>
                </div>
              )}

              {step === "details" && (
                <form data-testid="payment-details-step" onSubmit={createOrder} className="mt-6 space-y-4">
                  <input
                    data-testid="payment-name-input"
                    required
                    minLength={2}
                    placeholder="Full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm transition-colors"
                  />
                  <input
                    data-testid="payment-email-input"
                    required
                    type="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-white border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm transition-colors"
                  />
                  <input
                    data-testid="payment-phone-input"
                    required
                    minLength={5}
                    type="tel"
                    placeholder="Phone number"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-white border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm transition-colors"
                  />
                  {error && <p data-testid="payment-error" className="text-[#D95A3D] text-sm">{error}</p>}
                  <div className="border border-dashed border-[#041C3E]/40 bg-white p-3">
                    <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#162030]/70">Have a coupon code?</label>
                    <div className="mt-2 flex gap-2">
                      <input
                        data-testid="payment-coupon-input"
                        placeholder="Enter code"
                        value={coupon}
                        disabled={!!couponInfo}
                        onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                        className="flex-1 bg-[#F9F6F1] border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-3 py-2 text-sm uppercase disabled:opacity-60"
                      />
                      {couponInfo ? (
                        <button
                          type="button"
                          data-testid="payment-coupon-remove"
                          onClick={() => { setCouponInfo(null); setCoupon(""); setCouponError(""); }}
                          className="px-4 py-2 text-sm font-semibold border border-[#041C3E] text-[#041C3E] hover:bg-[#041C3E] hover:text-white transition-colors"
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid="payment-coupon-apply"
                          onClick={applyCoupon}
                          disabled={applyingCoupon || !coupon.trim()}
                          className="px-4 py-2 text-sm font-semibold bg-[#041C3E] text-white hover:bg-[#D95A3D] transition-colors disabled:opacity-50"
                        >
                          {applyingCoupon ? "…" : "Apply"}
                        </button>
                      )}
                    </div>
                    {couponInfo && (
                      <p data-testid="payment-coupon-applied" className="mt-2 text-xs font-semibold text-green-700">
                        Coupon {couponInfo.code} applied. New price: {effInr}{effUsd ? ` / ${effUsd}` : ""}.
                      </p>
                    )}
                    {couponError && <p data-testid="payment-coupon-error" className="mt-2 text-xs text-[#D95A3D]">{couponError}</p>}
                  </div>
                  <button
                    data-testid="payment-continue-button"
                    type="submit"
                    disabled={busy || !agreed}
                    className="w-full bg-[#D95A3D] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C24A2F] transition-colors duration-300 disabled:opacity-60"
                  >
                    {busy ? "Preparing donation…" : couponInfo?.inr_amount === 0 ? "Get free access" : tier.recurring ? `Donate ${effInr}/mo` : `Donate ${effInr}`}
                  </button>
                  <p className="flex items-center gap-2 text-xs text-[#162030]/60">
                    <Lock size={12} /> Donation charged in INR via Razorpay. Assessment unlocks instantly after your donation.
                  </p>
                </form>
              )}

              {step === "demo" && order && (
                <div className="mt-6 space-y-4">
                  <div className="border border-[#041C3E] bg-white p-4 text-sm">
                    <p className="font-bold uppercase tracking-[0.15em] text-xs text-[#162030]">Demo mode</p>
                    <p className="mt-2 text-[#162030]">
                      Razorpay keys are not configured yet, so this simulates the full checkout, signature
                      verification, and content unlock flow.
                    </p>
                    <p className="mt-2 font-mono text-xs text-[#162030]/50 break-all">{order.order_id}</p>
                  </div>
                  {error && <p className="text-[#D95A3D] text-sm">{error}</p>}
                  <button
                    data-testid="payment-demo-pay-button"
                    onClick={demoPay}
                    disabled={busy}
                    className="w-full bg-[#D95A3D] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C24A2F] transition-colors duration-300 disabled:opacity-60"
                  >
                    {busy ? "Verifying…" : `Donate ${effInr} (demo)`}
                  </button>
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
